<template>
  <div class="chart-container">
    <h3 class="title is-6">Invoice Status Distribution</h3>
    <div class="chart-wrapper">
      <canvas ref="statusChart"></canvas>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

export default {
  name: 'InvoiceStatusChart',
  props: {
    data: {
      type: Object,
      default: () => ({ status_distribution: [] })
    }
  },
  data() {
    return {
      chart: null,
      isCreating: false // Flag to prevent multiple simultaneous chart creations
    }
  },
  mounted() {
    // Add a small delay to ensure data is available and DOM is stable
    setTimeout(() => {
      this.$nextTick(() => {
        this.createChart();
      });
    }, 100);
  },
  watch: {
    data: {
      deep: true,
      handler() {
        // Add delay to ensure data is stable before updating
        setTimeout(() => {
          this.$nextTick(() => {
            this.updateChart();
          });
        }, 50);
      }
    }
  },
  beforeUnmount() {
    this.destroyChart();
  },
  methods: {
    createChart() {
      // Prevent multiple simultaneous chart creations
      if (this.isCreating) {
        console.log('InvoiceStatusChart: Chart creation already in progress');
        return;
      }
      
      this.isCreating = true;
      
      try {
        // Always destroy existing chart first to prevent conflicts
        this.destroyChart();
        
        // Validate data first
        if (!this.data || !this.data.status_distribution || this.data.status_distribution.length === 0) {
          console.log('InvoiceStatusChart: No valid data available for chart creation');
          return;
        }

        if (!this.$refs.statusChart) {
          console.log('InvoiceStatusChart: Canvas ref not available');
          return;
        }
        
        const canvas = this.$refs.statusChart;
        
        // Ensure canvas is properly mounted in DOM
        if (!canvas.parentNode) {
          console.log('InvoiceStatusChart: Canvas not mounted in DOM');
          return;
        }
        
        // Get context with error handling
        let ctx;
        try {
          ctx = canvas.getContext('2d');
        } catch (contextError) {
          console.error('InvoiceStatusChart: Error getting canvas context:', contextError);
          return;
        }
        
        if (!ctx) {
          console.log('InvoiceStatusChart: Could not get canvas context');
          return;
        }
        
        // Clear any existing content
        // eslint-disable-next-line no-self-assign
        canvas.width = canvas.width;
        
        const labels = this.data.status_distribution.map(item => item.status) || [];
        const values = this.data.status_distribution.map(item => item.count) || [];
        
        // Validate we have actual data
        if (labels.length === 0 || values.length === 0) {
          console.log('InvoiceStatusChart: No labels or values available');
          return;
        }
        
        console.log('InvoiceStatusChart: Creating chart with data:', { labels, values });
        
        this.chart = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: labels,
            datasets: [{
              data: values,
              backgroundColor: [
                '#0a3954',
                '#dc3545',
                '#dc3545',
                '#ffc107',
                '#082B3F',
                '#0a1821'
              ],
              borderWidth: 2,
              borderColor: '#ffffff'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false, // Disable all animations to prevent context conflicts
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  padding: 20,
                  usePointStyle: true
                }
              },
              tooltip: {
                callbacks: {
                  label: function(context) {
                    const total = context.dataset.data.reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
                    return `${context.label}: ${context.parsed} (${percentage}%)`;
                  }
                }
              }
            }
          }
        });
        
        console.log('InvoiceStatusChart: Chart created successfully');
      } catch (error) {
        console.error('InvoiceStatusChart: Error creating chart:', error);
      } finally {
        this.isCreating = false;
      }
    },
    updateChart() {
      if (!this.chart) {
        this.createChart();
        return;
      }
      
      try {
        const labels = this.data.status_distribution?.map(item => item.status) || [];
        const values = this.data.status_distribution?.map(item => item.count) || [];
        
        this.chart.data.labels = labels;
        this.chart.data.datasets[0].data = values;
        this.chart.update('none'); // Use 'none' animation mode for updates
      } catch (error) {
        console.error('Error updating status chart:', error);
        this.destroyChart();
        this.$nextTick(() => {
          this.createChart();
        });
      }
    },
    destroyChart() {
      if (this.chart) {
        try {
          // Stop any ongoing animations before destroying
          this.chart.stop();
          this.chart.destroy();
          console.log('InvoiceStatusChart: Chart destroyed successfully');
        } catch (error) {
          console.error('InvoiceStatusChart: Error destroying chart:', error);
        } finally {
          this.chart = null;
        }
      }
    }
  }
}
</script>

<style scoped>
.chart-container {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.chart-wrapper {
  position: relative;
  height: 300px;
}
</style>